import { useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { companyRepsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Trash2, Loader2, Copy, Mail, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Navigate, useLocation } from 'react-router-dom';

const getErrorMessage = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return 'An error occurred';
  if (typeof d === 'string') return d;
  return d.msg || JSON.stringify(d);
};

export default function CompanyReps() {
  const { isCompanyOwner, user } = useAuth();
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const allowed = isCompanyOwner();

  const fetchReps = async () => {
    setLoading(true);
    try {
      const res = await companyRepsApi.list();
      setReps(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (allowed) fetchReps();
  }, [allowed]);

  // iter167 Bug 5 — Auto-pop the Invite modal when the user lands here
  // via the Dashboard's "Invite your Representative" CTA
  // (/company/reps?invite=1).
  const location = useLocation();
  useEffect(() => {
    if (!allowed) return;
    const params = new URLSearchParams(location.search);
    if (params.get('invite') === '1') {
      setShowInvite(true);
    }
  }, [allowed, location.search]);

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleInvite = async () => {
    setInviting(true);
    try {
      const res = await companyRepsApi.invite(inviteEmail || null);
      setInviteResult(res.data);
      if (inviteEmail) toast.success(`Invitation emailed to ${inviteEmail}`);
      else toast.success('Invitation link generated');
      await fetchReps();
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally { setInviting(false); }
  };

  const closeInvite = () => {
    setShowInvite(false);
    setInviteEmail('');
    setInviteResult(null);
  };

  const handleRemove = async (rep) => {
    if (!confirm(`Remove ${rep.name} (${rep.email})? They will lose access immediately.`)) return;
    try {
      await companyRepsApi.remove(rep.id);
      toast.success('Rep removed');
      await fetchReps();
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied!'),
      () => toast.error('Copy failed'),
    );
  };

  const renderStatus = (rep) => {
    const status = rep.status || (rep.is_active ? 'active' : 'inactive');
    if (status === 'pending') return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Pending Approval</Badge>;
    if (status === 'inactive') return <Badge variant="outline" className="bg-slate-100 text-slate-700">Inactive</Badge>;
    return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Active</Badge>;
  };

  return (
    <DashboardLayout title="Manage Company Reps">
      <div className="space-y-6" data-testid="company-reps-page">
        <Card>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Company Representatives</h2>
                <p className="text-sm text-slate-500">Invite teammates to act on behalf of your company on ClinicalFlow.</p>
              </div>
            </div>
            <Button
              onClick={() => setShowInvite(true)}
              className="bg-blue-900 hover:bg-blue-950"
              data-testid="invite-rep-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Invite Rep
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table data-testid="reps-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
                  </TableCell></TableRow>
                )}
                {!loading && reps.filter(r => !r.is_company_owner).length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    No reps yet. Click "Invite Rep" to add one.
                  </TableCell></TableRow>
                )}
                {!loading && reps.filter(r => !r.is_company_owner).map((r) => (
                  <TableRow key={r.id} data-testid={`rep-row-${r.id}`}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-slate-600">{r.email}</TableCell>
                    <TableCell className="text-slate-600">{r.phone || '—'}</TableCell>
                    <TableCell>{renderStatus(r)}</TableCell>
                    {/* iter166h — Delete button removed per product spec.
                        Reps can only be deactivated via Admin, not by the
                        company owner inline. */}
                    <TableCell className="text-right text-xs text-slate-400">
                      —
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showInvite} onOpenChange={(o) => !o && closeInvite()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a Company Rep</DialogTitle>
              <DialogDescription>
                Generate a one-time invitation link valid for 7 days. Optionally provide an email and we'll send it directly.
              </DialogDescription>
            </DialogHeader>
            {!inviteResult ? (
              <div className="space-y-4">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>The invited rep will still need to be approved by an administrator after they complete registration.</span>
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="email"
                      className="pl-10"
                      placeholder="rep@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={closeInvite} disabled={inviting}>Cancel</Button>
                  <Button onClick={handleInvite} disabled={inviting} data-testid="generate-invite-btn">
                    {inviting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</> : (inviteEmail ? 'Send Invitation' : 'Generate Link')}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Invitation created. Share this link with your rep — it expires on{' '}
                  <strong>{new Date(inviteResult.expires_at).toLocaleString()}</strong>.
                </p>
                <div className="flex gap-2">
                  <Input value={inviteResult.invite_url} readOnly className="font-mono text-xs" data-testid="invite-url-output" />
                  <Button onClick={() => copyToClipboard(inviteResult.invite_url)} variant="outline">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <DialogFooter>
                  <Button onClick={closeInvite} data-testid="close-invite-btn">Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
